import { BlogPost } from "@/lib/blog";
import { CheckCircle, Star, TrendingUp, Zap, Target, BarChart3 } from "lucide-react";

interface BlogPostContentProps {
  post: BlogPost;
}

export function BlogPostContent({ post }: BlogPostContentProps) {
  const parseMarkdownContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      
      // Skip empty lines
      if (!line.trim()) {
        i++;
        continue;
      }

      // Code blocks
      if (line.startsWith('```')) {
        const codeLines: string[] = [];
        const language = line.substring(3).trim() || 'text';
        i++; // Skip opening ```
        
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // Skip closing ```
        
        elements.push(
          <div key={`code-${i}`} className="my-8 group">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {language} Code
              </span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-muted/80 to-muted border border-border/50 rounded-xl overflow-hidden">
              <pre className="p-6 overflow-x-auto">
                <code className="text-sm font-mono text-foreground leading-relaxed">
                  {codeLines.join('\n')}
                </code>
              </pre>
            </div>
          </div>
        );
        continue;
      }

      // Checklist items
      if (line.startsWith('- [ ]') || line.startsWith('- [x]')) {
        const isChecked = line.startsWith('- [x]');
        const content = line.substring(6);
        elements.push(
          <div key={`checklist-${i}`} className="flex items-start gap-3 my-3 p-3 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors">
            <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
              isChecked ? 'bg-primary border-primary' : 'border-muted-foreground'
            }`}>
              {isChecked && <CheckCircle className="h-3 w-3 text-white" />}
            </div>
            <span className={`text-sm ${isChecked ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
              {content.includes('**') ? 
                content.split('**').map((part, idx) => 
                  idx % 2 === 1 ? <strong key={idx} className="font-semibold">{part}</strong> : part
                ) : 
                content
              }
            </span>
          </div>
        );
        i++;
        continue;
      }

      // Process other elements
      const element = processLine(line, i);
      if (element) {
        elements.push(element);
      }
      i++;
    }

    return elements;
  };

  const processLine = (line: string, index: number): JSX.Element | null => {
    // H1 Headers
    if (line.startsWith('# ')) {
      return (
        <h1 key={index} className="text-4xl md:text-5xl font-bold mb-8 text-foreground bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent leading-tight">
          {line.substring(2)}
        </h1>
      );
    }

    // H2 Headers with icons
    if (line.startsWith('## ')) {
      const title = line.substring(3);
      const getIcon = () => {
        if (title.toLowerCase().includes('stratég') || title.toLowerCase().includes('avancé')) return <Target className="h-6 w-6" />;
        if (title.toLowerCase().includes('optim') || title.toLowerCase().includes('création')) return <TrendingUp className="h-6 w-6" />;
        if (title.toLowerCase().includes('perform') || title.toLowerCase().includes('analytic')) return <BarChart3 className="h-6 w-6" />;
        if (title.toLowerCase().includes('audience') || title.toLowerCase().includes('campaign')) return <Zap className="h-6 w-6" />;
        if (title.toLowerCase().includes('foundation') || title.toLowerCase().includes('creative')) return <Star className="h-6 w-6" />;
        return <Star className="h-6 w-6" />;
      };

      return (
        <div key={index} className="flex items-center gap-4 mt-12 mb-6 pb-3 border-b border-primary/20">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
            {getIcon()}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            {title}
          </h2>
        </div>
      );
    }

    // H3 Headers
    if (line.startsWith('### ')) {
      return (
        <h3 key={index} className="text-xl font-semibold mt-8 mb-4 text-foreground flex items-center gap-3 group">
          <div className="w-1 h-6 bg-gradient-to-b from-primary to-primary/60 rounded-full group-hover:scale-110 transition-transform"></div>
          <span className="group-hover:text-primary transition-colors">{line.substring(4)}</span>
        </h3>
      );
    }

    // Blockquotes (Pro Tips)
    if (line.startsWith('> ')) {
      const content = line.substring(2);
      return (
        <div key={index} className="my-6 p-6 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/60"></div>
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-full bg-primary/20 text-primary">
              <Zap className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-primary mb-1">Pro Tip</div>
              <div className="text-foreground font-medium leading-relaxed">
                {content.includes('**') ? 
                  content.split('**').map((part, idx) => 
                    idx % 2 === 1 ? <strong key={idx} className="text-primary">{part}</strong> : part
                  ) : 
                  content
                }
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Bullet points with enhanced styling
    if (line.startsWith('- ') && !line.startsWith('- [ ]') && !line.startsWith('- [x]')) {
      const content = line.substring(2);
      const isHighlight = content.startsWith('**') && content.includes('**');
      
      return (
        <div key={index} className="flex items-start gap-4 my-3 group hover:bg-primary/5 p-2 rounded-lg transition-colors">
          <div className="mt-2 p-1">
            <div className="w-2 h-2 bg-primary rounded-full group-hover:scale-125 transition-transform"></div>
          </div>
          <p className="text-muted-foreground flex-1 leading-relaxed">
            {content.includes('**') ? 
              content.split('**').map((part, idx) => 
                idx % 2 === 1 ? <strong key={idx} className="text-foreground font-semibold">{part}</strong> : part
              ) : 
              content
            }
          </p>
        </div>
      );
    }

    // Regular paragraphs with better formatting
    if (line.trim() && !line.startsWith('#') && !line.startsWith('-') && !line.startsWith('>') && !line.startsWith('```')) {
      return (
        <p key={index} className="text-muted-foreground leading-relaxed mb-6 text-lg">
          {line.includes('**') ? 
            line.split('**').map((part, idx) => 
              idx % 2 === 1 ? <strong key={idx} className="text-foreground font-semibold">{part}</strong> : part
            ) : 
            line
          }
        </p>
      );
    }

    return null;
  };
  
  const renderContent = () => {
    if (post.status === 'draft') {
      return (
        <div className="bg-gradient-to-br from-card/50 to-card border border-border/50 rounded-xl p-8 animate-fade-in">
          <h2 className="text-2xl font-bold mb-4 text-foreground">Aperçu du contenu</h2>
          <div className="whitespace-pre-line text-muted-foreground">
            {post.content.substring(0, 500)}...
          </div>
          <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <p className="text-sm text-primary font-medium">
              📝 Cet article est en cours de rédaction par nos experts Amazon SPN. 
              Le contenu complet sera bientôt disponible avec des stratégies exclusives 
              et des exemples concrets.
            </p>
          </div>
        </div>
      );
    }
    
    // For published articles, parse and render enhanced content
    const parsedContent = parseMarkdownContent(post.content);
    
    return (
      <article className="prose prose-lg max-w-none">
        <div className="space-y-6 animate-fade-in">
          {parsedContent}
          
          {/* Action CTA at the end */}
          <div className="mt-16 p-8 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border border-primary/20 rounded-2xl">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-4">
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-foreground">
                Prêt à transformer vos performances DSP ?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Nos experts Amazon SPN ont développé des stratégies DSP qui génèrent en moyenne 300% de ROAS. 
                Découvrez comment appliquer ces techniques à votre marque.
              </p>
              <button 
                className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all duration-200 hover:scale-105"
                onClick={() => window.open("https://cal.com/cosmoswave", "_blank")}
              >
                Audit DSP gratuit
                <TrendingUp className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section className="py-16 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background pointer-events-none" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl pointer-events-none" />
      
      <div className="container mx-auto px-6 relative">
        <div className="max-w-4xl mx-auto" id="cw-article-body">
          {renderContent()}
        </div>
      </div>
    </section>
  );
}